import { _js } from '@ifixit/utils/localize';
import { toast } from '@ifixit/ui/components/toast';
import { Utils } from 'Shared/utils';
import { Auth } from 'Shared/auth';
import { StatusNotice } from 'Shared/status_notice';
import { FrameModules } from 'Shared/frame_modules';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';

FrameModules.add('TeamProfileFrameModule', () => {
   TeamProfileBox.initialize();

   document.querySelectorAll('.repairServicesShowMore').forEach(el => {
      el.addEventListener('click', ev => {
         ev.preventDefault();
         const services = document.getElementById('repairServices');
         services.style.display = services.style.display === 'none' ? '' : 'none';
      });
   });

   if (App.showStatus) {
      const notice = new StatusNotice();
      const inviteStatus = document.getElementById('inviteStatus');
      inviteStatus.append(notice.element);
      document.getElementById('bodyTop').prepend(inviteStatus);

      if (App.success) {
         notice.good(_js('You have been added to this team!'));
      } else {
         notice.error(App.inviteError || _js('There was an error adding you to this team'));
      }
   }

   if (App.promptLogin) {
      if (App.hasUser) {
         Auth.login({
            from: 'navLogin',
            message: _js('Logging in will reload the page.'),
            reload: !Utils.hasUnsavedChanges(),
            register: false,
         });
      } else {
         window.location.assign('/Join');
      }
   }

   if (document.getElementById('inviteContainer')) {
      new InviteForm();
   }
});

class InviteForm {
   constructor() {
      this.inviteContainer = document.getElementById('inviteContainer');
      this.modalLink = document.getElementById('modalLink');
      this.userList = document.getElementById('userListContainer');
      this.forceInvites = document.getElementById('forceInvites');
      this.emails = document.getElementById('inviteEmails');
      this.message = document.getElementById('inviteMessage');
      this.submitButton = document.getElementById('inviteSubmit');
      this.closeButton = document.getElementById('inviteClose');
      this.emailsHelp = document.getElementById('inviteEmailsHelp');
      this.invalidEmails = document.getElementById('inviteInvalidEmails');
      this.noticeDiv = document.getElementById('statusNoticeDiv');

      this.modalLink.addEventListener('click', ev => {
         ev.preventDefault();

         this.emails.addEventListener('focus', () => {
            this.emailsHelp.style.display = '';
            this.invalidEmails.style.display = 'none';
         });

         this.setupNotice('title');

         import('Shared/modal').then(({ Modal }) => {
            Modal.open({
               type: 'element',
               element: this.inviteContainer,
            });
         });

         this.inviteContainer.style.display = '';
      });

      this.submitButton.addEventListener('click', () => {
         const validEmails = this.matchEmails();
         if (validEmails.length <= 0) {
            return;
         }

         const teamid = this.inviteContainer.dataset.teamid;
         const message = this.message.value;
         const force = this.forceInvites ? this.forceInvites.checked : false;

         this.processInvites(teamid, validEmails, message, force);
      });

      this.closeButton.addEventListener('click', () => {
         import('Shared/modal').then(({ Modal }) => {
            Modal.close();
         });
      });
   }

   matchEmails() {
      const list = this.emails.value;
      const matches = list.match(/([\w.-]+@[\w.-]+\.[a-z]{2,4})/gi);
      if (!matches) {
         this.invalidEmailsError();
         return [];
      }

      const validEmails = [];
      for (const match of matches) {
         if (validEmails.includes(match)) {
            continue;
         }
         validEmails.push(match);
      }

      return validEmails;
   }

   invalidEmailsError() {
      this.emailsHelp.style.display = 'none';
      this.invalidEmails.style.display = '';
      this.emails.style.borderColor = '#8a1c1c';
      this.emails.style.background = '#efd0d0';
   }

   setupNotice(type, message) {
      const notice = new StatusNotice();
      this.noticeDiv.replaceChildren();
      this.noticeDiv.append(notice.element);
      switch (type) {
         case 'error': {
            notice.error(_js('Remaining emails are either invalid or already in team.'));
            break;
         }
         case 'serious_error': {
            notice.error(_js('Operation Failed') + (message ? ': ' + message : ''));
            break;
         }
         case 'send': {
            notice.notice(_js('Sending Invites...'));
            break;
         }
         case 'title':
         default: {
            notice.good(App.title);
            break;
         }
      }
   }

   processInvites(teamid, emails, message, force) {
      if (emails.length > App.teamInvite.maxInvitesPerFormSubmission) {
         this.setupNotice('serious_error', _js(App.teamInvite.error.maxInvitesExceeded));
         return;
      }

      this.lockForm();
      this.setupNotice('send');

      new Request.AjaxIO('sendInvites', {
         onSuccess: response => {
            if (response) {
               this.setupNotice('error');
               this.unlockForm();
               this.emails.value = response;
            } else {
               import('Shared/modal').then(({ Modal }) => {
                  Modal.close();
               });

               this.resetForm();
               toast.success(_js('Invites sent!'));
            }
         },

         onError: response => {
            this.setupNotice('serious_error', response.error);
            this.unlockForm();
         },
      }).send(teamid, emails, message, force);
   }

   resetForm() {
      this.unlockForm();
      this.emailsHelp.style.display = '';
      this.invalidEmails.style.display = 'none';
      this.message.value = '';
   }

   lockForm() {
      this.emails.disabled = true;
      this.message.disabled = true;
      if (this.forceInvites !== null) {
         this.forceInvites.disabled = true;
      }
      this.submitButton.disabled = true;
      this.submitButton.classList.add('buttonLinkDisabled');
      this.closeButton.disabled = true;
      this.closeButton.classList.add('buttonLinkDisabled');
   }

   unlockForm() {
      this.emails.disabled = false;
      this.message.disabled = false;
      if (this.forceInvites !== null) {
         this.forceInvites.disabled = false;
      }
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('buttonLinkDisabled');
      this.closeButton.disabled = false;
      this.closeButton.classList.remove('buttonLinkDisabled');
   }
}

const TeamProfileBox = {
   initialize() {
      const reqLogin = document.getElementById('reqLogin');
      if (reqLogin) {
         this.requireLogin();
      }

      this.leaveTeam();
      this.joinTeam();
   },

   requireLogin() {
      Auth.login({
         from: 'navLogin',
         message: _js('Logging in will reload the page.'),
         reload: true,
      });
   },

   leaveTeam() {
      document.querySelectorAll('.js-leave-team-button').forEach(el => {
         el.addEventListener('click', ev => {
            ev.preventDefault();
            const memberCount = el.dataset.membercount;
            const result =
               memberCount == 1
                  ? confirm(
                       _js(
                          'Warning! You are the last member of this team. Leaving this team will delete the team. Do you want to continue?'
                       )
                    )
                  : confirm(
                       _js(
                          "Warning! Leaving this team will remove all of your contributions and activities from your team's history. Do you want to continue?"
                       )
                    );

            if (result) {
               new Request.API_2_0(App.teamApi.leaveUrl + App.userid, {
                  method: App.teamApi.leaveMethod,
                  statusPanelMessage: _js('Leaving...'),
                  onSuccess() {
                     window.location.search = '';
                  },
                  onFailure(xhr, message) {
                     alert(message);
                  },
               }).send();
            }
         });
      });
   },

   joinTeam() {
      document.querySelectorAll('.js-join-team-button').forEach(el => {
         el.addEventListener('click', ev => {
            ev.preventDefault();

            Auth.required({
               reload: false,
               message: _js('Log in to join this team.'),
               onAuthorize(response) {
                  const userid = response?.userid || App.userid;
                  const joinCode = el.dataset.joincode;
                  const codeParam = joinCode ? '?join_code=' + joinCode : '';
                  const joinUrl = App.teamApi.joinUrl + userid + codeParam;

                  new Request.API_2_0(joinUrl, {
                     method: App.teamApi.joinMethod,
                     statusPanelMessage: _js('Joining...'),
                     onSuccess() {
                        trackAnalyticsCustomEvent({
                           eventCategory: 'Join Team',
                           eventAction: 'Join Team - Form - Submit',
                        });
                        window.location.reload();
                     },
                     onFailure(xhr, message) {
                        alert(message);
                     },
                  }).send();
               },
            });
         });
      });
   },
};
